import React, { memo } from 'react'
import { useNavigate } from 'react-router'

import { useHandler, useIsMounted } from '@surglogs-app/hooks'
import { usePostTokenRefresh } from '@surglogs-app/network'
import { usePrompt } from '@surglogs-app/prompt'
import { getAuthLogoutRoute } from '@surglogs-app/routing'

import { decodeJwtToken, verifyAndDecodeJwtToken } from '../helpers'
import { useAuthToken } from '../hooks'

import { PrivateRouteSession } from './PrivateRouteSession'
import { SessionInactivityDialog } from './SessionInactivityDialog'

interface PrivateRouteSessionProps {
  apiVersion: string
  referrer?: string
  organization?: string
}

export const PrivateRouteSessionWrapper = memo(
  (props: PrivateRouteSessionProps) => {
    const navigate = useNavigate()
    const { getRefreshToken, setToken, isTokenAboutToExpire, getAccessToken } =
      useAuthToken()

    const {
      mutateAsync: refreshJwtTokenMutate,
      isPending: isLoadingJwtRefreshToken,
    } = usePostTokenRefresh()

    const getNewRefreshToken = async () => {
      const refreshToken = getRefreshToken()
      const currentAccessToken = getAccessToken()
      const decodedCurrentAccessToken = currentAccessToken
        ? decodeJwtToken(currentAccessToken)
        : null

      if (refreshToken && decodedCurrentAccessToken) {
        const response = await refreshJwtTokenMutate({
          refresh_token: refreshToken,
          scope: decodedCurrentAccessToken.surglogs_scope,
          sub_id: decodedCurrentAccessToken.sub,
        })

        if (response) {
          const { access_token: newAccessToken, expires_in: newExpiresIn } =
            response

          if ('jwks_url' in response) {
            const decodedToken = await verifyAndDecodeJwtToken(
              newAccessToken,
              response.jwks_url,
            )

            if (!decodedToken) {
              navigate(getAuthLogoutRoute(), {
                replace: true,
              })

              throw new Error('Failed to verify or decode JWT token')
            }
          }

          setToken(newAccessToken, refreshToken, newExpiresIn)
        }
      }
    }

    const getNewRefreshTokenIfNeeded = async () => {
      if (!isLoadingJwtRefreshToken && isTokenAboutToExpire()) {
        getNewRefreshToken()
      }
    }

    const isMounted = useIsMounted()
    const [prompt] = usePrompt({ autoClose: false })
    const showSessionInactivityDialog = useHandler(
      (onConfirm: () => void, onCancel: () => Promise<void>) =>
        prompt(({ answer }) => (
          <SessionInactivityDialog
            {...{
              onCancel: async () => {
                await onCancel()
                ;(answer as () => void)()
              },
              onConfirm: () => {
                onConfirm()
                ;(answer as () => void)()
              },
            }}
          />
        )),
    )

    return (
      <PrivateRouteSession
        {...props}
        {...{
          isMounted,
          showSessionInactivityDialog,
          refreshToken: getNewRefreshToken,
          refreshTokenIfNeeded: getNewRefreshTokenIfNeeded,
        }}
      />
    )
  },
)
